import { useRef } from "react";
import { useSnackbar } from "notistack";
import { Button, Typography, useTheme } from "@monolith-forensics/monolith-ui";

import { useRelayInfo } from "../../hooks";
import RelayAPI from "../../../../../api/RelayAPI";
import { UploadIcon } from "lucide-react";
import { SectionContainer } from "./SectionContainer";

export const TenantLogo: React.FC = () => {
  const { relayInfo, refetch } = useRelayInfo();
  const imgRef = useRef<HTMLImageElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  if (!relayInfo) return null;

  const handleUpdateLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Check if files exist and if file is an image
    if (!e.target.files || !e.target.files[0].type.includes("image")) {
      enqueueSnackbar(`File is not an image`, {
        variant: "error",
      });
      return;
    }

    // Check that file size is less than 100KB
    if (e.target.files[0].size > 100000) {
      enqueueSnackbar(`File is too large, must be less than 100KB`, {
        variant: "error",
      });
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(e.target.files[0]);

    const logoData = await new Promise((resolve) => {
      reader.onload = () => {
        resolve(reader.result);
      };
    });

    // Set image source to base64 data
    // loads image into imgRef right away
    if (imgRef.current) {
      if (typeof logoData === "string") {
        imgRef.current.src = logoData;
      }
    }

    // get base64 data form reader result
    const base64 =
      typeof logoData === "string" ? logoData.split(";base64,")[1] : "";

    await RelayAPI.updateTenantLogo({
      tenant_uuid: relayInfo.tenant_uuid,
      tenant_logo: base64,
    });

    enqueueSnackbar("Relay tenant logo updated successfully", {
      variant: "success",
    });

    refetch();
  };

  return (
    <SectionContainer>
      <div>
        <Typography variant="h3" display="inline">
          Tenant Logo
        </Typography>
        <div className="message">
          This is the logo that will display in Relay.
        </div>
      </div>
      <div style={{ maxHeight: 125 }}>
        <img
          ref={imgRef}
          src={relayInfo.tenant_logo}
          alt="logo"
          style={{
            width: "auto",
            maxHeight: "125px",
            display: relayInfo.tenant_logo ? "block" : "none",
          }}
        />
        {!relayInfo.tenant_logo && (
          <div style={{ color: theme.palette.text.secondary }}>
            No logo uploaded
          </div>
        )}
      </div>
      <div>
        <input
          ref={inputRef}
          accept="*"
          style={{ display: "none" }}
          id="update-relay-logo"
          type="file"
          onChange={handleUpdateLogo}
          multiple={false}
        />
        <label htmlFor="update-relay-logo">
          <Button
            size="xs"
            variant="contained"
            color="primary"
            leftSection={<UploadIcon size={16} />}
            style={{ marginLeft: "auto" }}
            onClick={() => inputRef.current?.click()}
          >
            Upload New Logo
          </Button>
        </label>
      </div>
    </SectionContainer>
  );
};
